import { Filter, Search, X } from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { Checkbox } from '../../../shared/components/ui/checkbox';
import { Input } from '../../../shared/components/ui/input';
import { cn } from '../../../shared/utils/cn';
import { CategoryFilterDropdown } from './CategoryFilterDropdown';
import { SpecialtyFilterDropdown } from './SpecialtyFilterDropdown';
import { LocationSearch } from './LocationSearch';
import type { Category, ProviderType, SearchFilters, ServiceRange, Specialty, UserRole } from '../types';

type ProviderDirectoryFiltersProps = {
  filters: SearchFilters;
  categories: Category[];
  specialties: Specialty[];
  isTaxonomyLoading: boolean;
  locationAddress: string;
  providerNameInput: string;
  providerType?: ProviderType;
  currentUserRole?: UserRole;
  onCategoryChange: (categoryIds: string[]) => void;
  onSpecialtyChange: (specialtyIds: string[]) => void;
  onServiceRangeToggle: (range: ServiceRange) => void;
  onLocationSelect: (location: { address: string; latitude: number; longitude: number; countyId?: string; stateCode?: string } | null) => void;
  onProviderNameChange: (name: string) => void;
  onProviderTypeChange?: (providerType: ProviderType) => void;
};

export const ProviderDirectoryFilters = ({
  filters,
  categories,
  specialties,
  isTaxonomyLoading,
  locationAddress,
  providerNameInput,
  providerType = filters.providerType ?? 'SERVICE_PROVIDER',
  currentUserRole,
  onCategoryChange,
  onSpecialtyChange,
  onServiceRangeToggle,
  onLocationSelect,
  onProviderNameChange,
  onProviderTypeChange,
}: ProviderDirectoryFiltersProps) => {
  const isFinancialAdvisorMode = providerType === 'FINANCIAL_ADVISOR';
  const canToggleProviderType = currentUserRole === 'SERVICE_PROVIDER';

  return (
    <Card
      className="p-6 sticky top-24 max-h-[calc(100vh-6rem)] overflow-y-auto"
      data-testid="provider-directory-filters"
    >
      <div className="flex items-center gap-2 mb-6">
        <Filter className="w-5 h-5 text-[#026857]" />
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
      </div>

      <div className="space-y-6">
        {canToggleProviderType && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-3 block">
              Provider Type
            </label>
            <div
              className="flex w-full rounded-full bg-[#026857] p-0.5"
              role="group"
              aria-label="Provider type"
            >
              <button
                type="button"
                className={cn(
                  'min-w-0 flex-1 rounded-full px-2 py-1.5 text-center text-[11px] font-semibold leading-tight transition-colors sm:text-xs',
                  providerType === 'SERVICE_PROVIDER'
                    ? 'bg-white text-[#026857] shadow-sm'
                    : 'text-white hover:bg-white/10',
                )}
                aria-pressed={providerType === 'SERVICE_PROVIDER'}
                onClick={() => onProviderTypeChange?.('SERVICE_PROVIDER')}
              >
                Service Providers
              </button>
              <button
                type="button"
                className={cn(
                  'min-w-0 flex-1 rounded-full px-2 py-1.5 text-center text-[11px] font-semibold leading-tight transition-colors sm:text-xs',
                  isFinancialAdvisorMode
                    ? 'bg-white text-[#026857] shadow-sm'
                    : 'text-white hover:bg-white/10',
                )}
                aria-pressed={isFinancialAdvisorMode}
                onClick={() => onProviderTypeChange?.('FINANCIAL_ADVISOR')}
              >
                Financial Advisors
              </button>
            </div>
          </div>
        )}

        {/* Feature 017: Provider Name Search */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-3 block">
            Search by Name
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name..."
              value={providerNameInput}
              onChange={(e) => onProviderNameChange(e.target.value)}
              className="pl-10 pr-10"
            />
            {providerNameInput && (
              <button
                onClick={() => onProviderNameChange('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {!isFinancialAdvisorMode && (
          <>
            {/* Category Filter */}
            <CategoryFilterDropdown
              categories={categories}
              selectedCategoryIds={filters.categoryIds || []}
              onChange={onCategoryChange}
              isLoading={isTaxonomyLoading}
            />

            {/* Specialty Filter */}
            <SpecialtyFilterDropdown
              specialties={specialties}
              categories={categories}
              selectedSpecialtyIds={filters.specialtyIds || []}
              onChange={onSpecialtyChange}
              isLoading={isTaxonomyLoading}
            />

            {/* Feature 017: Expertise filter removed to simplify search */}

            {/* Service Range Checkboxes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">
                Service Type
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="in-home"
                    checked={(filters.serviceRanges || []).includes('IN_HOME')}
                    onCheckedChange={() => onServiceRangeToggle('IN_HOME')}
                  />
                  <label
                    htmlFor="in-home"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    In-Home
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="virtual"
                    checked={(filters.serviceRanges || []).includes('VIRTUAL')}
                    onCheckedChange={() => onServiceRangeToggle('VIRTUAL')}
                  />
                  <label
                    htmlFor="virtual"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    Virtual
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="in-office"
                    checked={(filters.serviceRanges || []).includes('IN_OFFICE')}
                    onCheckedChange={() => onServiceRangeToggle('IN_OFFICE')}
                  />
                  <label
                    htmlFor="in-office"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    In-Office
                  </label>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Location Search */}
        <LocationSearch
          onLocationSelect={onLocationSelect}
          value={locationAddress}
          placeholder="Enter city or ZIP code"
        />
      </div>
    </Card>
  );
};

